import api from './api';

export interface Company {
  _id?: string;
  name: string;
  email_id: string;
  contact_no: string;
  country_code?: string;
  is_email_verify?: boolean;
  website?: string;
  address?: string;
  logo?: string;
  industry?: string;
  about_us?: string;
  contactPerson?: string;
  founded_date?: Date;
  video_link?: string;
  social_media_profiles?: Array<{
    platform: string;
    link: string;
  }>;
  company_size?: '1-50' | '51-100' | '101-500' | '501-1000' | '1001-5000' | '5001-10000' | '10001+';
  policy?: string;
  is_deleted?: boolean;
  status: 'active' | 'inactive' | 'pending';
  createdAt?: Date;
  updatedAt?: Date;
}

export interface CompanyListResponse {
  success: boolean;
  count: number;
  pagination: {
    total: number;
    page: number;
    pages: number;
    limit: number;
  };
  data: Company[];
}

export interface CompanyResponse {
  success: boolean;
  data: Company;
}

export interface CompanyQueryParams {
  page?: number;
  limit?: number;
  status?: string;
  search?: string;
}

class CompanyService {
  /**
   * Get all companies with pagination and filtering
   */
  async getCompanies(params: CompanyQueryParams = {}): Promise<CompanyListResponse> {
    try {
      const response = await api.get<CompanyListResponse>('/companies', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching companies:', error);
      throw error;
    }
  }

  /**
   * Get a single company by ID
   */
  async getCompany(id: string): Promise<Company> {
    try {
      const response = await api.get<CompanyResponse>(`/companies/${id}`);
      return response.data.data;
    } catch (error) {
      console.error(`Error fetching company ${id}:`, error);
      throw error;
    }
  }

  /**
   * Create a new company
   */
  async createCompany(companyData: Company): Promise<Company> {
    try {
      const response = await api.post<CompanyResponse>('/companies', companyData);
      return response.data.data;
    } catch (error) {
      console.error('Error creating company:', error);
      throw error;
    }
  }

  /**
   * Update an existing company
   */
  async updateCompany(id: string, companyData: Partial<Company>): Promise<Company> {
    try {
      const response = await api.put<CompanyResponse>(`/companies/${id}`, companyData);
      return response.data.data;
    } catch (error) {
      console.error(`Error updating company ${id}:`, error);
      throw error;
    }
  }

  /**
   * Delete a company
   */
  async deleteCompany(id: string): Promise<void> {
    try {
      await api.delete(`/companies/${id}`);
    } catch (error) {
      console.error(`Error deleting company ${id}:`, error);
      throw error;
    }
  }

  /**
   * Update company status
   */
  async updateCompanyStatus(id: string, status: 'active' | 'inactive' | 'pending'): Promise<Company> {
    try {
      const response = await api.patch<CompanyResponse>(`/companies/${id}/status`, { status });
      return response.data.data;
    } catch (error) {
      console.error(`Error updating company status ${id}:`, error);
      throw error;
    }
  }

  /**
   * Set a new login password for the company's account
   */
  async updateCompanyPassword(id: string, password: string): Promise<void> {
    try {
      await api.patch(`/companies/${id}/password`, { password });
    } catch (error) {
      console.error(`Error updating company password ${id}:`, error);
      throw error;
    }
  }
}

export default new CompanyService();